import React, { createContext, useContext, useReducer, useEffect, useCallback, useMemo } from 'react';
import { 
  projetsService,
  personnesService,
  rattachementsService,
  tempsPlanifiesService,
  tempsConsommesService,
  authService
} from '../services/api';
import { 
  startOfMonth, 
  endOfMonth, 
  startOfQuarter, 
  endOfQuarter
} from 'date-fns';

// État initial
const initialState = {
  projets: [],
  personnes: [],
  rattachements: [],
  tempsPlanifies: [],
  tempsConsommes: [],
  loading: false,
  error: null,
  selectedWeek: null,
  selectedProjet: null,
  selectedPersonne: null,
  filters: {
    statut: '',
    role: '',
    searchTerm: '',
    categorie: '',
  },
  // Nouvelle gestion de période
  periodSelection: {
    type: 'preset', // 'preset', 'custom', 'single'
    preset: 'current_month', // 'current_month', 'current_quarter', 'fiscal_year', '6_months'
    startDate: null,
    endDate: null,
    singleDate: null
  },
  notifications: [],
  // Authentification
  auth: {
    isAuthenticated: false,
    user: null,
    token: null,
    loading: false,
  },
  // Onboarding
  onboarding: {
    showOnboarding: false,
    hasCompletedOnboarding: false,
  },
};

// Types d'actions
const actionTypes = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_PROJETS: 'SET_PROJETS',
  SET_PERSONNES: 'SET_PERSONNES',
  SET_RATTACHEMENTS: 'SET_RATTACHEMENTS',
  SET_TEMPS_PLANIFIES: 'SET_TEMPS_PLANIFIES',
  SET_TEMPS_CONSOMMES: 'SET_TEMPS_CONSOMMES',
  SET_SELECTED_WEEK: 'SET_SELECTED_WEEK',
  SET_SELECTED_PROJET: 'SET_SELECTED_PROJET',
  SET_SELECTED_PERSONNE: 'SET_SELECTED_PERSONNE',
  SET_FILTERS: 'SET_FILTERS',
  SET_PERIOD_SELECTION: 'SET_PERIOD_SELECTION',
  ADD_NOTIFICATION: 'ADD_NOTIFICATION',
  REMOVE_NOTIFICATION: 'REMOVE_NOTIFICATION',
  ADD_PROJET: 'ADD_PROJET',
  UPDATE_PROJET: 'UPDATE_PROJET',
  DELETE_PROJET: 'DELETE_PROJET',
  ADD_PERSONNE: 'ADD_PERSONNE',
  UPDATE_PERSONNE: 'UPDATE_PERSONNE',
  DELETE_PERSONNE: 'DELETE_PERSONNE',
  ADD_RATTACHEMENT: 'ADD_RATTACHEMENT',
  DELETE_RATTACHEMENT: 'DELETE_RATTACHEMENT',
  UPSERT_TEMPS_PLANIFIE: 'UPSERT_TEMPS_PLANIFIE',
  UPSERT_TEMPS_CONSOMME: 'UPSERT_TEMPS_CONSOMME',
  // Authentification
  SET_AUTH_LOADING: 'SET_AUTH_LOADING',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGOUT: 'LOGOUT',
  UPDATE_USER: 'UPDATE_USER',
  // Onboarding
  SHOW_ONBOARDING: 'SHOW_ONBOARDING',
  HIDE_ONBOARDING: 'HIDE_ONBOARDING',
  COMPLETE_ONBOARDING: 'COMPLETE_ONBOARDING',
};

// Réducteur
const appReducer = (state, action) => {
  switch (action.type) {
    case actionTypes.SET_LOADING:
      return { ...state, loading: action.payload };
    
    case actionTypes.SET_ERROR:
      return { ...state, error: action.payload, loading: false };
    
    case actionTypes.SET_PROJETS:
      return { ...state, projets: action.payload, loading: false };
    
    case actionTypes.SET_PERSONNES:
      return { ...state, personnes: action.payload, loading: false };
    
    case actionTypes.SET_RATTACHEMENTS:
      return { ...state, rattachements: action.payload, loading: false };
    
    case actionTypes.SET_TEMPS_PLANIFIES:
      return { ...state, tempsPlanifies: action.payload, loading: false };
    
    case actionTypes.SET_TEMPS_CONSOMMES:
      return { ...state, tempsConsommes: action.payload, loading: false };
    
    case actionTypes.SET_SELECTED_WEEK:
      return { ...state, selectedWeek: action.payload };
    
    case actionTypes.SET_SELECTED_PROJET:
      return { ...state, selectedProjet: action.payload };
    
    case actionTypes.SET_SELECTED_PERSONNE:
      return { ...state, selectedPersonne: action.payload };
    
    case actionTypes.SET_FILTERS:
      return { ...state, filters: { ...state.filters, ...action.payload } };
    
    case actionTypes.SET_PERIOD_SELECTION:
      return { ...state, periodSelection: action.payload };
    
    case actionTypes.ADD_NOTIFICATION:
      return {
        ...state,
        notifications: [...state.notifications, action.payload]
      };
    
    case actionTypes.REMOVE_NOTIFICATION:
      return {
        ...state,
        notifications: state.notifications.filter(n => n.id !== action.payload)
      };
    
    case actionTypes.ADD_PROJET:
      return {
        ...state,
        projets: {
          ...state.projets,
          data: [...(state.projets?.data || []), action.payload]
        }
      };
    
    case actionTypes.UPDATE_PROJET:
      return {
        ...state,
        projets: {
          ...state.projets,
          data: state.projets?.data?.map(p => p.id === action.payload.id ? action.payload : p) || []
        }
      };
    
    case actionTypes.DELETE_PROJET:
      return {
        ...state,
        projets: {
          ...state.projets,
          data: state.projets?.data?.filter(p => p.id !== action.payload) || []
        }
      };
    
    case actionTypes.ADD_PERSONNE:
      return {
        ...state,
        personnes: {
          ...state.personnes,
          data: [...(state.personnes?.data || []), action.payload]
        }
      };
    
    case actionTypes.UPDATE_PERSONNE:
      return {
        ...state,
        personnes: {
          ...state.personnes,
          data: state.personnes?.data?.map(p => p.id === action.payload.id ? action.payload : p) || []
        }
      };
    
    case actionTypes.DELETE_PERSONNE:
      return {
        ...state,
        personnes: {
          ...state.personnes,
          data: state.personnes?.data?.filter(p => p.id !== action.payload) || []
        }
      };
    
    case actionTypes.ADD_RATTACHEMENT:
      return {
        ...state,
        rattachements: {
          ...state.rattachements,
          data: [...(state.rattachements?.data || []), action.payload]
        }
      };
    
    case actionTypes.DELETE_RATTACHEMENT:
      return {
        ...state,
        rattachements: {
          ...state.rattachements,
          data: state.rattachements?.data?.filter(r => r.id !== action.payload) || []
        }
      };
    
    case actionTypes.UPSERT_TEMPS_PLANIFIE:
      const tempsPlanifiesUpdated = (state.tempsPlanifies?.data || []).filter(
        tp => !(tp.id_projet === action.payload.id_projet && tp.semaine === action.payload.semaine)
      );
      return {
        ...state,
        tempsPlanifies: {
          ...state.tempsPlanifies,
          data: [...tempsPlanifiesUpdated, action.payload]
        }
      };
    
    case actionTypes.UPSERT_TEMPS_CONSOMME:
      const tempsConsommesUpdated = (state.temps_consommes || []).filter(
        tc => !(tc.id_projet === action.payload.id_projet && 
               tc.id_personne === action.payload.id_personne && 
               tc.semaine === action.payload.semaine)
      );
      return {
        ...state,
        tempsConsommes: {
          ...state.temps_consommes,
          data: [...tempsConsommesUpdated, action.payload]
        }
      };
    
    case actionTypes.SET_AUTH_LOADING:
      return {
        ...state,
        auth: { ...state.auth, loading: action.payload }
      };
    
    case actionTypes.LOGIN_SUCCESS:
      return {
        ...state,
        auth: {
          isAuthenticated: true,
          user: action.payload.user,
          token: action.payload.token,
          loading: false,
        }
      };
    
    case actionTypes.LOGOUT:
      return {
        ...state,
        auth: {
          isAuthenticated: false,
          user: null,
          token: null,
          loading: false,
        }
      };
    
    case actionTypes.UPDATE_USER:
      return {
        ...state,
        auth: { ...state.auth, user: action.payload }
      };
    
    case actionTypes.SHOW_ONBOARDING:
      return {
        ...state,
        onboarding: { ...state.onboarding, showOnboarding: true }
      };
    
    case actionTypes.HIDE_ONBOARDING:
      return {
        ...state,
        onboarding: { ...state.onboarding, showOnboarding: false }
      };
    
    case actionTypes.COMPLETE_ONBOARDING:
      localStorage.setItem('hasCompletedOnboarding', 'true');
      return {
        ...state,
        onboarding: { 
          showOnboarding: false, 
          hasCompletedOnboarding: true 
        }
      };
    
    default:
      return state;
  }
};

// Création du contexte
const AppContext = createContext();

// Hook personnalisé pour utiliser le contexte
export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

// Fournisseur du contexte
export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Mémorisation de loadPersonneById pour éviter les re-rendus en boucle
  const loadPersonneById = useCallback(async (id) => {
    try {
      dispatch({ type: actionTypes.SET_LOADING, payload: true });
      const personne = await personnesService.getById(id);
      dispatch({ type: actionTypes.SET_LOADING, payload: false });
      return personne.data; // Extract the person from API response wrapper
    } catch (error) {
      dispatch({ type: actionTypes.SET_ERROR, payload: error.message });
      dispatch({ type: actionTypes.SET_LOADING, payload: false });
      return null;
    }
  }, []);

  // Actions (mémorisées pour éviter les boucles infinies)
  const actions = useMemo(() => ({
    setLoading: (loading) => dispatch({ type: actionTypes.SET_LOADING, payload: loading }),
    setError: (error) => dispatch({ type: actionTypes.SET_ERROR, payload: error }),
    
    // Notifications
    addNotification: (notification) => {
      const notificationId = Date.now();
      dispatch({ type: actionTypes.ADD_NOTIFICATION, payload: { ...notification, id: notificationId } });
      setTimeout(() => {
        actions.removeNotification(notificationId);
      }, 5000);
    },
    removeNotification: (id) => dispatch({ type: actionTypes.REMOVE_NOTIFICATION, payload: id }),
    
    // Filtres et sélections
    setFilters: (filters) => dispatch({ type: actionTypes.SET_FILTERS, payload: filters }),
    setSelectedWeek: (week) => dispatch({ type: actionTypes.SET_SELECTED_WEEK, payload: week }),
    setSelectedProjet: (projet) => dispatch({ type: actionTypes.SET_SELECTED_PROJET, payload: projet }),
    setSelectedPersonne: (personne) => dispatch({ type: actionTypes.SET_SELECTED_PERSONNE, payload: personne }),
    
    // Gestion de période
    setPeriodSelection: (periodData) => dispatch({ type: actionTypes.SET_PERIOD_SELECTION, payload: periodData }),
    
    // Utilitaires de période
    getPeriodDates: () => {
      const { periodSelection } = state;
      const now = new Date();
      
      if (periodSelection.type === 'preset') {
        switch (periodSelection.preset) {
          case 'current_month':
            return { start: startOfMonth(now), end: endOfMonth(now) };
          case 'current_quarter':
            return { start: startOfQuarter(now), end: endOfQuarter(now) };
          case 'fiscal_year': {
            // Exercice en cours : du 1er juillet dernier au 30 juin prochain
            const currentYear = now.getFullYear();
            const currentMonth = now.getMonth(); // 0-11
            
            // Si nous sommes avant le 1er juillet (mois 6), l'exercice a commencé l'année dernière
            if (currentMonth < 6) {
              const start = new Date(currentYear - 1, 6, 1); // 1er juillet année dernière
              const end = new Date(currentYear, 5, 30); // 30 juin année courante
              return { start, end };
            } else {
              // Si nous sommes le 1er juillet ou après, l'exercice a commencé cette année
              const start = new Date(currentYear, 6, 1); // 1er juillet année courante
              const end = new Date(currentYear + 1, 5, 30); // 30 juin année prochaine
              return { start, end };
            }
          }
          case '6_months':
            return { start: startOfMonth(now), end: endOfMonth(new Date(now.getFullYear(), now.getMonth() + 5, now.getDate())) };
          default:
            return { start: startOfMonth(now), end: endOfMonth(now) };
        }
      } else if (periodSelection.type === 'custom') {
        return { 
          start: periodSelection.startDate ? new Date(periodSelection.startDate) : startOfMonth(now), 
          end: periodSelection.endDate ? new Date(periodSelection.endDate) : endOfMonth(now) 
        };
      } else if (periodSelection.type === 'single') {
        const date = periodSelection.singleDate ? new Date(periodSelection.singleDate) : now;
        return { start: date, end: date };
      }
      
      return { start: startOfMonth(now), end: endOfMonth(now) };
    },
    
    // Projets
    loadProjets: async () => {
      try {
        actions.setLoading(true);
        const projets = await projetsService.getAll();
        dispatch({ type: actionTypes.SET_PROJETS, payload: projets });
      } catch (error) {
        actions.setError(error.message);
      }
    },
    
    createProjet: async (projet) => {
      try {
        actions.setLoading(true);
        const newProjet = await projetsService.create(projet);
        dispatch({ type: actionTypes.ADD_PROJET, payload: newProjet });
        actions.addNotification({ type: 'success', message: 'Projet créé avec succès' });
      } catch (error) {
        actions.setError(error.message);
        actions.addNotification({ type: 'error', message: 'Erreur lors de la création du projet' });
      }
    },
    
    updateProjet: async (id, projet) => {
      try {
        actions.setLoading(true);
        const updatedProjet = await projetsService.update(id, projet);
        dispatch({ type: actionTypes.UPDATE_PROJET, payload: updatedProjet });
        actions.addNotification({ type: 'success', message: 'Projet mis à jour avec succès' });
      } catch (error) {
        actions.setError(error.message);
        actions.addNotification({ type: 'error', message: 'Erreur lors de la mise à jour du projet' });
      }
    },
    
    deleteProjet: async (id) => {
      try {
        actions.setLoading(true);
        await projetsService.delete(id);
        dispatch({ type: actionTypes.DELETE_PROJET, payload: id });
        actions.addNotification({ type: 'success', message: 'Projet supprimé avec succès' });
      } catch (error) {
        actions.setError(error.message);
        actions.addNotification({ type: 'error', message: 'Erreur lors de la suppression du projet' });
      }
    },
    
    // Personnes
    loadPersonnes: async () => {
      try {
        actions.setLoading(true);
        const personnes = await personnesService.getAll();
        dispatch({ type: actionTypes.SET_PERSONNES, payload: personnes });
      } catch (error) {
        actions.setError(error.message);
      }
    },

    loadPersonneById,
    
    createPersonne: async (personne) => {
      try {
        actions.setLoading(true);
        const newPersonne = await personnesService.create(personne);
        dispatch({ type: actionTypes.ADD_PERSONNE, payload: newPersonne });
        actions.addNotification({ type: 'success', message: 'Personne créée avec succès' });
      } catch (error) {
        actions.setError(error.message);
        actions.addNotification({ type: 'error', message: 'Erreur lors de la création de la personne' });
      }
    },
    
    updatePersonne: async (id, personne) => {
      try {
        actions.setLoading(true);
        const updatedPersonne = await personnesService.update(id, personne);
        dispatch({ type: actionTypes.UPDATE_PERSONNE, payload: updatedPersonne });
        actions.addNotification({ type: 'success', message: 'Personne mise à jour avec succès' });
      } catch (error) {
        actions.setError(error.message);
        actions.addNotification({ type: 'error', message: 'Erreur lors de la mise à jour de la personne' });
      }
    },
    
    deletePersonne: async (id) => {
      try {
        actions.setLoading(true);
        // Instead of deleting, we deactivate the person
        const updatedPersonne = await personnesService.update(id, { actif: false });
        dispatch({ type: actionTypes.UPDATE_PERSONNE, payload: updatedPersonne });
        actions.addNotification({ type: 'success', message: 'Personne désactivée avec succès' });
        actions.setLoading(false);
      } catch (error) {
        actions.setError(error.message);
        actions.addNotification({ type: 'error', message: 'Erreur lors de la désactivation de la personne' });
        actions.setLoading(false);
      }
    },

    reactivatePersonne: async (id) => {
      try {
        actions.setLoading(true);
        const updatedPersonne = await personnesService.update(id, { actif: true });
        dispatch({ type: actionTypes.UPDATE_PERSONNE, payload: updatedPersonne });
        actions.addNotification({ type: 'success', message: 'Personne réactivée avec succès' });
        actions.setLoading(false);
      } catch (error) {
        actions.setError(error.message);
        actions.addNotification({ type: 'error', message: 'Erreur lors de la réactivation de la personne' });
        actions.setLoading(false);
      }
    },
    
    // Rattachements
    loadRattachements: async () => {
      try {
        actions.setLoading(true);
        const rattachements = await rattachementsService.getAll();
        dispatch({ type: actionTypes.SET_RATTACHEMENTS, payload: rattachements });
      } catch (error) {
        actions.setError(error.message);
      }
    },
    
    createRattachement: async (rattachement) => {
      try {
        actions.setLoading(true);
        const newRattachement = await rattachementsService.create(rattachement);
        dispatch({ type: actionTypes.ADD_RATTACHEMENT, payload: newRattachement });
        actions.addNotification({ type: 'success', message: 'Rattachement créé avec succès' });
      } catch (error) {
        actions.setError(error.message);
        actions.addNotification({ type: 'error', message: 'Erreur lors de la création du rattachement' });
      }
    },
    
    deleteRattachement: async (id) => {
      try {
        actions.setLoading(true);
        await rattachementsService.delete(id);
        dispatch({ type: actionTypes.DELETE_RATTACHEMENT, payload: id });
        actions.addNotification({ type: 'success', message: 'Rattachement supprimé avec succès' });
      } catch (error) {
        actions.setError(error.message);
        actions.addNotification({ type: 'error', message: 'Erreur lors de la suppression du rattachement' });
      }
    },
    
    // Temps planifiés
    loadTempsPlanifies: async (projetId) => {
      try {
        actions.setLoading(true);
        const tempsPlanifies = await tempsPlanifiesService.getByProjet(projetId);
        dispatch({ type: actionTypes.SET_TEMPS_PLANIFIES, payload: tempsPlanifies });
      } catch (error) {
        actions.setError(error.message);
      }
    },

    // Charger tous les temps planifiés avec filtres optionnels
    loadAllTempsPlanifies: async (params = {}) => {
      try {
        actions.setLoading(true);
        const tempsPlanifies = await tempsPlanifiesService.getAll(params);
        dispatch({ type: actionTypes.SET_TEMPS_PLANIFIES, payload: tempsPlanifies });
      } catch (error) {
        actions.setError(error.message);
      }
    },
    
    saveTempsPlanifie: async (data, semaine, temps) => {
      try {
        actions.setLoading(true);
        // Support both old and new signatures
        if (typeof data === 'object' && data.id_projet) {
          const tempsPlanifie = await tempsPlanifiesService.upsert(
            data.id_projet, 
            data.semaine, 
            {
              analyse_design: data.analyse_design,
              build: data.build,
              test: data.test
            }
          );
          dispatch({ type: actionTypes.UPSERT_TEMPS_PLANIFIE, payload: tempsPlanifie });
        } else {
          // Legacy signature: first param is projetId
          const tempsPlanifie = await tempsPlanifiesService.upsert(data, semaine, temps);
          dispatch({ type: actionTypes.UPSERT_TEMPS_PLANIFIE, payload: tempsPlanifie });
        }
        actions.addNotification({ type: 'success', message: 'Temps planifiés sauvegardés avec succès' });
      } catch (error) {
        actions.setError(error.message);
        actions.addNotification({ type: 'error', message: 'Erreur lors de la sauvegarde des temps planifiés' });
      }
    },
    
    // Temps consommés
    loadTempsConsommes: async (projetId) => {
      try {
        actions.setLoading(true);
        const tempsConsommes = await tempsConsommesService.getByProjet(projetId);
        dispatch({ type: actionTypes.SET_TEMPS_CONSOMMES, payload: tempsConsommes });
      } catch (error) {
        actions.setError(error.message);
      }
    },
    
    loadAllTempsConsommes: async (params = {}) => {
      try {
        actions.setLoading(true);
        const tempsConsommes = await tempsConsommesService.getAll(params);
        dispatch({ type: actionTypes.SET_TEMPS_CONSOMMES, payload: tempsConsommes });
      } catch (error) {
        actions.setError(error.message);
      }
    },
    
    saveTempsConsomme: async (data, personneId, semaine, joursConsommes, facturable, commentaire, phase) => {
      try {
        actions.setLoading(true);
        // Support both old and new signatures
        if (typeof data === 'object' && data.id_projet) {
          const tempsConsomme = await tempsConsommesService.upsert(
            data.id_projet, 
            data.id_personne, 
            data.semaine, 
            data.jours_consommes, 
            data.facturable,
            data.commentaire,
            data.phase
          );
          dispatch({ type: actionTypes.UPSERT_TEMPS_CONSOMME, payload: tempsConsomme });
        } else {
          // Legacy signature: first param is projetId
          const tempsConsomme = await tempsConsommesService.upsert(
            data, personneId, semaine, joursConsommes, facturable, commentaire, phase
          );
          dispatch({ type: actionTypes.UPSERT_TEMPS_CONSOMME, payload: tempsConsomme });
        }
        actions.addNotification({ type: 'success', message: 'Temps consommés sauvegardés avec succès' });
      } catch (error) {
        actions.setError(error.message);
        actions.addNotification({ type: 'error', message: 'Erreur lors de la sauvegarde des temps consommés' });
      }
    },

    // Authentification
    register: async (userData) => {
      try {
        dispatch({ type: actionTypes.SET_AUTH_LOADING, payload: true });
        const response = await authService.register(userData);
        dispatch({ type: actionTypes.SET_AUTH_LOADING, payload: false });
        actions.addNotification({ type: 'success', message: 'Inscription réussie ! Vous pouvez maintenant vous connecter.' });
        return response;
      } catch (error) {
        dispatch({ type: actionTypes.SET_AUTH_LOADING, payload: false });
        actions.setError(error.message);
        actions.addNotification({ type: 'error', message: error.message });
        throw error;
      }
    },

    login: async (credentials) => {
      try {
        dispatch({ type: actionTypes.SET_AUTH_LOADING, payload: true });
        const { token, user } = await authService.login(credentials);
        dispatch({ type: actionTypes.LOGIN_SUCCESS, payload: { token, user } });
        actions.addNotification({ type: 'success', message: `Bienvenue ${user.nom} !` });
        
        // Charger les données de l'application après connexion réussie
        loadInitialData();
        
        return { token, user };
      } catch (error) {
        dispatch({ type: actionTypes.SET_AUTH_LOADING, payload: false });
        actions.setError(error.message);
        actions.addNotification({ type: 'error', message: error.message });
        throw error;
      }
    },

    logout: () => {
      authService.logout();
      dispatch({ type: actionTypes.LOGOUT });
      actions.addNotification({ type: 'info', message: 'Vous avez été déconnecté' });
    },

    verifyAuth: async () => {
      try {
        dispatch({ type: actionTypes.SET_AUTH_LOADING, payload: true });
        
        const token = authService.initializeAuth();
        console.log('Token trouvé:', !!token);
        
        if (token) {
          const response = await authService.verify();
          console.log('Réponse verify:', response);
          
          // La réponse a la structure { user: utilisateur }
          const user = response.user;
          console.log('Utilisateur extrait:', user);
          
          if (user) {
            dispatch({ type: actionTypes.LOGIN_SUCCESS, payload: { token, user } });
            console.log('Authentification réussie, utilisateur connecté');
            return true;
          }
        }
        console.log('Pas de token ou utilisateur invalide');
        dispatch({ type: actionTypes.SET_AUTH_LOADING, payload: false });
        return false;
      } catch (error) {
        console.error('Erreur de vérification auth:', error);
        authService.logout();
        dispatch({ type: actionTypes.LOGOUT });
        return false;
      }
    },

    updateProfile: async (profileData) => {
      try {
        dispatch({ type: actionTypes.SET_AUTH_LOADING, payload: true });
        const updatedUser = await authService.updateProfile(profileData);
        dispatch({ type: actionTypes.UPDATE_USER, payload: updatedUser });
        dispatch({ type: actionTypes.SET_AUTH_LOADING, payload: false });
        actions.addNotification({ type: 'success', message: 'Profil mis à jour avec succès' });
        return updatedUser;
      } catch (error) {
        dispatch({ type: actionTypes.SET_AUTH_LOADING, payload: false });
        actions.setError(error.message);
        actions.addNotification({ type: 'error', message: error.message });
        throw error;
      }
    },

    changePassword: async (passwordData) => {
      try {
        dispatch({ type: actionTypes.SET_AUTH_LOADING, payload: true });
        await authService.changePassword(passwordData);
        dispatch({ type: actionTypes.SET_AUTH_LOADING, payload: false });
        actions.addNotification({ type: 'success', message: 'Mot de passe modifié avec succès' });
      } catch (error) {
        dispatch({ type: actionTypes.SET_AUTH_LOADING, payload: false });
        actions.setError(error.message);
        actions.addNotification({ type: 'error', message: error.message });
        throw error;
      }
    },

    // Onboarding
    showOnboarding: () => dispatch({ type: actionTypes.SHOW_ONBOARDING }),
    hideOnboarding: () => dispatch({ type: actionTypes.HIDE_ONBOARDING }),
    completeOnboarding: () => dispatch({ type: actionTypes.COMPLETE_ONBOARDING }),
    
    checkOnboardingStatus: () => {
      const hasCompleted = localStorage.getItem('hasCompletedOnboarding') === 'true';
      if (hasCompleted) {
        dispatch({ type: actionTypes.COMPLETE_ONBOARDING });
      }
      return hasCompleted;
    },
  }), [dispatch, loadPersonneById]);

  // Chargement initial des données de manière optimisée
  const loadInitialData = useCallback(async () => {
    try {
      actions.setLoading(true);
      
      // Charger les données essentielles en parallèle sans relations pour de meilleures performances
      await Promise.all([
        actions.loadProjets(),
        actions.loadPersonnes()
      ]);
      
      // Charger les rattachements après car ils dépendent des projets et personnes
      await actions.loadRattachements();
      
    } catch (error) {
      actions.setError('Erreur lors du chargement initial des données');
    }
  }, [actions]);

  // Vérification de l'authentification au chargement
  useEffect(() => {
    const initAuth = async () => {
      const isAuthenticated = await actions.verifyAuth();
      if (isAuthenticated) {
        loadInitialData();
      }
    };
    
    initAuth();
  }, [actions, loadInitialData]);

  // Charger les données quand l'utilisateur se connecte
  useEffect(() => {
    if (state.auth.isAuthenticated && state.auth.user && (!state.projets?.data || state.projets.data.length === 0)) {
      loadInitialData();
    }
  }, [state.auth.isAuthenticated, state.auth.user, loadInitialData]);



  // Valeur du contexte (mémorisée pour les performances)
  const value = useMemo(() => ({
    state,
    actions,
    // Sélecteurs utiles
    getProjetById: (id) => state.projets?.data?.find(p => p.id === parseInt(id)),
    getPersonneById: (id) => state.personnes?.data?.find(p => p.id === parseInt(id)),
    loadPersonneById: actions.loadPersonneById,
    getRattachementsByProjet: (projetId) => state.rattachements?.data?.filter(r => r.id_projet === parseInt(projetId)),
    getTempsPlanifiesByProjet: (projetId) => state.tempsPlanifies?.data?.filter(tp => tp.id_projet === parseInt(projetId)),

    getFilteredProjets: () => {
      let filteredProjets = state.projets?.data;
      
      if (state.filters.statut) {
        filteredProjets = filteredProjets.filter(p => p.statut === state.filters.statut);
      }
      
      if (state.filters.categorie) {
        filteredProjets = filteredProjets.filter(p => p.categorie === state.filters.categorie);
      }
      
      if (state.filters.searchTerm) {
        const searchTerm = state.filters.searchTerm.toLowerCase();
        filteredProjets = filteredProjets.filter(p => 
          p.nom.toLowerCase().includes(searchTerm) || 
          p.description.toLowerCase().includes(searchTerm)
        );
      }
      
      return filteredProjets;
    },
    getFilteredPersonnes: () => {
      let filteredPersonnes = state.personnes?.data || [];
      
      if (state.filters.role) {
        filteredPersonnes = filteredPersonnes.filter(p => p.role === state.filters.role);
      }
      
      if (state.filters.searchTerm) {
        const searchTerm = state.filters.searchTerm.toLowerCase();
        filteredPersonnes = filteredPersonnes.filter(p => 
          p.nom.toLowerCase().includes(searchTerm) || 
          p.prenom.toLowerCase().includes(searchTerm) ||
          p.email.toLowerCase().includes(searchTerm)
        );
      }
      
      return filteredPersonnes;
    },
  }), [state, actions, loadPersonneById]);

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
}; 